import { Box } from '@chakra-ui/react';
import { ArticleNodeGroup } from '@texas/api/endpoints/articlesApi';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { nodeGroupIsPopulated } from '@texas/utils/helpers/nodeGroupHelpers';
import { StandardVersions } from './standard/StandardVersions';
import { MatrixVersions } from './matrix/MatrixVersions';
import { useMultiSelectSamples } from '@texas/components/shared/samples/useMultiSelectSamples';
import { createContext } from 'react';
import { TexasHeader } from '@texas/components/shared/TexasHeader';
import { useTranslation } from 'react-i18next';
import { VersionOverviewProvider } from './shared/VersionOverviewContext';
import { queryStringToObject } from '@texas/utils/helpers/commonHelpers';

interface Props {
  variantId: number;
  articleNodeGroup: ArticleNodeGroup;
}

interface VersionsQueryParams {
  branchId?: number;
  supplierId?: number;
}

export const MultiSelectContext = createContext<ReturnType<
  typeof useMultiSelectSamples
> | null>(null);

export function Versions({ variantId, articleNodeGroup }: Props) {
  const sampleMultiSelect = useMultiSelectSamples();
  const { t } = useTranslation();
  const queryParams = queryStringToObject<VersionsQueryParams>();
  const initialBranchId = getQueryId(queryParams?.branchId);
  const initialSupplierId = getQueryId(queryParams?.supplierId);

  return (
    <BodyOverride>
      <MultiSelectContext.Provider value={sampleMultiSelect}>
        <VersionOverviewProvider
          initialFilters={{
            ...(initialBranchId && { branchIds: [initialBranchId] }),
            ...(initialSupplierId && { supplierIds: [initialSupplierId] }),
          }}
        >
          <Box pb={32} pt={8}>
            <Box mb={4}>
              <TexasHeader text={t('navigation.versions')} />
            </Box>
            {!nodeGroupIsPopulated(articleNodeGroup) ? (
              <StandardVersions
                variantId={variantId}
                initialBranchId={initialBranchId}
              />
            ) : (
              <MatrixVersions
                variantId={variantId}
                articleNodeGroup={articleNodeGroup}
                initialBranchId={initialBranchId}
              />
            )}
          </Box>
        </VersionOverviewProvider>
      </MultiSelectContext.Provider>
    </BodyOverride>
  );
}

function getQueryId(value: unknown) {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0
    ? value
    : undefined;
}
